import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApiClients } from 'src/hooks/useAppClients';
import { getAbnormalVitals } from 'utils/lib/helpers/vitals/utils';
import { GetVitalsResponseData } from 'utils/lib/types/api/chart-data/get-vitals.types';
import { useAppointmentData } from '../../../stores/appointment/appointment.store';

export const useGetVitals = (encounterId: string | undefined): UseQueryResult<GetVitalsResponseData, Error> => {
  const { oystehrZambda } = useApiClients();
  const queryKey = encounterId ? [`current-encounter-vitals-${encounterId}`] : [];
  return useQuery({
    queryKey,
    queryFn: async () => {
      if (oystehrZambda && encounterId) {
        const result = await oystehrZambda.zambda.execute({
          id: 'get-vitals',
          encounterId,
          currentOrHistorical: 'current',
        });
        // todo: make this strictly typed once there is a common api file defining endpoints available
        return result.output as GetVitalsResponseData;
      }

      throw new Error('api client not defined or encounter id is not provided');
    },
    enabled: Boolean(encounterId) && Boolean(oystehrZambda),
  });
};

export const useGetHistoricalVitals = (
  encounterId: string | undefined
): UseQueryResult<GetVitalsResponseData, Error> => {
  const { oystehrZambda } = useApiClients();
  const queryKey = encounterId ? [`historical-encounter-vitals-${encounterId}`] : [];
  return useQuery({
    queryKey,
    queryFn: async () => {
      if (oystehrZambda && encounterId) {
        const result = await oystehrZambda.zambda.execute({
          id: 'get-vitals',
          encounterId,
          currentOrHistorical: 'historical',
        });
        // todo: make this strictly typed once there is a common api file defining endpoints available
        return result.output as GetVitalsResponseData;
      }

      throw new Error('api client not defined or encounter id is not provided');
    },
    enabled: Boolean(encounterId) && Boolean(oystehrZambda),
  });
};

export function useGetAbnormalVitals(): GetVitalsResponseData | undefined {
  const {
    resources: { encounter },
  } = useAppointmentData();

  const { data: encounterVitals } = useGetVitals(encounter?.id);

  return useMemo(() => {
    if (!encounterVitals) return undefined;
    return getAbnormalVitals(encounterVitals);
  }, [encounterVitals]);
}
